<template>
    <b-card no-body border-variant="white">
        <b-card-header header-bg-variant="white" header-border-variant="white">
            <b-row>
                <b-col class="mb-sm-2 mb-0 text-nowrap">
                    <h5 class="text-muted text-uppercase font-weight-bold">
                        {{ $t('profile.table.details.checkInsChart') }}
                    </h5>
                </b-col>
            </b-row>
        </b-card-header>
        <b-card-body>
            <check-ins-chart style="height: 300px" :indicator="indicator" />
        </b-card-body>
        <b-card no-body border-variant="white">
            <div class="ml-4 mt-sm-2 mt-0">
                <h5 class="text-muted text-uppercase font-weight-bold">
                    {{ $t('profile.table.details.expectancy') }}
                </h5>
            </div>
            <b-card-body>
                <b-list-group class="list-group-accent">
                    <div class="d-flex flex-wrap flex-lg-row justify-content-between mb-2">
                        <div class="list-group-item-accent-light rounded-left text-muted mr-2">
                            <div class="ml-2">
                                <small>{{ `${$t('profile.table.target')}` }}</small>
                                <br />
                                <b>{{ indicatorTarget }}</b>
                            </div>
                        </div>
                        <div class="list-group-item-accent-light rounded-left text-muted mr-2">
                            <div class="ml-2">
                                <small>{{ $t('profile.targetsTable.targetExpectancy') }}</small>
                                <br />
                                <b>{{ indicatorExpectancy }}</b>
                            </div>
                        </div>
                        <div class="list-group-item-accent-light rounded-left text-muted mr-2">
                            <div class="ml-2">
                                <small>{{ $t('targets.currentResult') }}</small>
                                <br />
                                <h5>
                                    <b>{{ indicatorCurrentResult }}</b>
                                </h5>
                            </div>
                        </div>
                        <div class="list-group-item-accent-light rounded-left text-muted mr-2">
                            <div class="ml-2">
                                <small>{{ $t('targets.expectation') }}</small>
                                <br />
                                <h5>
                                    <b>{{ indicatorExpectation }}</b>
                                </h5>
                            </div>
                        </div>

                        <div class="list-group-item-accent-light rounded-left text-muted">
                            <div class="ml-2">
                                <small>{{ $t('form.label.forecast') }}</small>
                                <br />
                                <span class="h5 font-weight-bold">{{ indicatorForecast }}</span>
                                <b-button
                                    id="btn-target-forecast-edit"
                                    variant="outline-primary"
                                    @click="openCheckInSimulatedModal"
                                    :disabled="!isAllowedToEdit"
                                    class="float-right btn-ghost-primary btn-pill btn-sm ml-3"
                                >
                                    {{ $t('form.button.edit') }}
                                </b-button>
                            </div>
                        </div>
                        <div class="list-group-item-accent-light rounded-left text-muted">
                            <div class="ml-2">
                                <small>{{ $t('targets.expectationsAndHeadlightRule') }}</small>
                                <br />
                                <b>{{ $t(`targets.visualizationType.${indicator.visualizationType}`) }}</b>
                                <b-button
                                    id="btn-target-expectations-edit"
                                    variant="outline-primary"
                                    @click="openExpectationModal"
                                    :disabled="!isAllowedToEdit"
                                    class="float-right btn btn-ghost-primary btn-pill btn-sm"
                                >
                                    {{ $t('form.button.edit') }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                    <b-list-group-item
                        class="list-group-item-accent-light my-1 py-2"
                        v-for="expectation in kpiCheckins.accomplisheds_by_month"
                        :key="expectation.id"
                    >
                        <b-row no-gutters>
                            <b-col cols="6" xl="2" class="my-auto" align="left">
                                <div class="flex" :id="`date-${expectation.date}`">
                                    <i
                                        class="mr-1 fas"
                                        :class="[
                                            expectation.unlocked
                                                ? 'text-success fa-lock-open'
                                                : 'text-secondary fa-lock',
                                        ]"
                                    />
                                    <strong class="text-muted">
                                        {{ $t(`months.${getNameOfTheMonthByIndex(expectation.date)}.short`) }}
                                    </strong>
                                </div>
                            </b-col>

                            <b-col cols="6" xl="2" class="order-xl-last d-flex justify-content-end" align="center">
                                <b-dropdown
                                    :id="`dropdown-${expectation.id}`"
                                    variant="link"
                                    class="float-xl-none float-xs-right"
                                    :no-caret="true"
                                    :dropleft="true"
                                    :disabled="disableRetroactiveCheckInDropdown"
                                >
                                    <template slot="button-content">
                                        <i class="fas fa-bars" />
                                    </template>
                                    <b-dropdown-item
                                        :disabled="!showUnlockExpectationCheckIn"
                                        @click="toggleUnlocked(expectation)"
                                    >
                                        <i class="fas" :class="[expectation.unlocked ? 'fa-lock' : 'fa-lock-open']" />
                                        {{ expectationLockButtonLabel(expectation) | capitalize }}
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        v-if="showCheckInDropdownItem"
                                        @click="retroactiveCheckIn(expectation)"
                                        :disabled="disableCheckInButton(expectation)"
                                        :class="{
                                            'not-allowed': disableCheckInButton(expectation),
                                        }"
                                    >
                                        <i class="fa fa-calendar-check-o" />
                                        {{ $t('checkIn.checkIn') }}
                                    </b-dropdown-item>
                                </b-dropdown>
                                <b-button
                                    :id="`popover-checkin-list-${expectation.id}`"
                                    class="text-primary"
                                    variant="link"
                                    v-b-toggle="`collapse-expectation-checkins-${expectation.id}`"
                                >
                                    <i class="fas fa-calendar-alt" />
                                </b-button>
                                <b-popover
                                    :target="`popover-checkin-list-${expectation.id}`"
                                    placement="left"
                                    :content="$t('targets.checkinFromExpectation')"
                                    triggers="hover"
                                />
                            </b-col>
                            <b-col md="6" lg="3" xl="2" class="my-auto" align="center">
                                <small class="text-muted">
                                    <i class="icon-calendar mr-1" />
                                    {{ getDate(expectation.date) }}
                                </small>
                            </b-col>
                            <b-col md="6" lg="3" xl="2" class="my-auto" align="center">
                                <small class="text-muted">
                                    <i class="icon-graph mr-1" />
                                    {{ $t('profile.table.details.expectancy') }}
                                    {{ indicatorDetailsExpectancy(expectation.expectation) }}
                                </small>
                            </b-col>

                            <b-col md="6" lg="3" xl="2" class="my-auto" align="center">
                                <small class="text-muted">
                                    <i class="icon-graph mr-1" />
                                    {{ indicatorExpectationResultLabel(expectation) }}
                                </small>
                            </b-col>
                            <b-col md="6" lg="3" xl="2" class="my-auto" align="center">
                                <small class="text-muted">
                                    <i class="icon-chart mr-1" />
                                    {{ $t('profile.table.details.progress') }}
                                    <b>{{ indicatorDetailsAchieved(expectation) }}</b>
                                </small>
                                <progress-bar
                                    :value="expectation.headlight.accomplishedPercentage"
                                    :color="expectation.headlight.color"
                                />
                            </b-col>
                        </b-row>
                        <checkins-list
                            :expectationId="expectation.id"
                            :checkins="expectation.checkins"
                            :simulatedCheckins="expectation.simulatedCheckins"
                            :indicatorType="indicatorType"
                            :indicatorId="indicator.id"
                            :indicatorLevel="indicator.level"
                            :currentEventPermissions="currentUser.permissions"
                            :isIndicatorOwnerOrManager="isIndicatorOwnerOrManager"
                            :isAdmin="isAdmin"
                            :isAllowedToEdit="isAllowedToEdit"
                            :canEditCheckIn="disableCheckInButton(expectation)"
                            @update-parent="updateParentEmitter"
                            :isFocalPoint="isCurrentUserFocalPoint"
                        />
                    </b-list-group-item>
                </b-list-group>
            </b-card-body>
        </b-card>
    </b-card>
</template>

<script>
import { updateLockUnlockExpectation } from './components/expectation/api'
import CheckInsChart from '@/views/target/components/check-ins-chart'
import { formatNumber, formatPercentage } from '@/lib/utils'
import { getIndexOfTheMonthFromString, getStringFromDateWithoutTime, getNameOfTheMonthByIndex } from '@/lib/date'
import ProgressBar from '@/views/components/progress-bar/ProgressBar'
import Swal from 'sweetalert2'
import i18n from '@/i18n'
import notification, { success, danger } from '@/lib/notification'
import { findIndex } from 'lodash'
import CheckinsList from './components/checkins-list'
import { userPermissionsMixin } from '@/mixins'

notification.init()

export default {
    name: 'Details',
    props: ['indicator', 'isAllowedToEdit'],
    mixins: [userPermissionsMixin],
    components: {
        CheckInsChart,
        ProgressBar,
        CheckinsList,
    },
    methods: {
        updateParentEmitter(event) {
            this.$emit('update-parent', event)
        },
        getNameOfTheMonthByIndex(string) {
            return getNameOfTheMonthByIndex(getIndexOfTheMonthFromString(string, 'YYYY-MM-DD'))
        },
        formatNumber(number) {
            return formatNumber(number)
        },
        formatPercentage(number) {
            return formatPercentage(number)
        },
        getDate(date) {
            return getStringFromDateWithoutTime(date, 'YYYY-MM-DD')
        },
        openExpectationModal() {
            this.$router.push({
                path: this.$route.fullPath + '/expectation',
            })
        },
        openCheckInSimulatedModal() {
            this.$router.push({
                path: this.$route.fullPath + '/checkin-simulated',
            })
        },
        toggleUnlocked(expectation) {
            const lockOrUnlock = expectation.unlocked ? i18n.t('block') : i18n.t('unblock')
            const referenceDate = i18n.t(`months.${this.getNameOfTheMonthByIndex(expectation.date)}.long`)
            Swal.fire({
                title: i18n.t('profile.table.details.swalConfirm.title', {
                    lockOrUnlock: lockOrUnlock,
                    referenceDate: referenceDate,
                }),
                type: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: i18n.t('notification.yes'),
                cancelButtonText: i18n.t('notification.no'),
            }).then((result) => {
                if (result.value) {
                    this.setLockUnlockExpectation(expectation, referenceDate)
                }
            })
        },
        setLockUnlockExpectation(expectation, referenceDate) {
            const indicatorId = expectation.indicator_id
            const kpiCheckinId = expectation.id

            updateLockUnlockExpectation(indicatorId, kpiCheckinId, this.indicatorType)
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    const expectationIndex = findIndex(this.kpiCheckins.accomplisheds_by_month, { id: data.id })

                    this.kpiCheckins.accomplisheds_by_month[expectationIndex].unlocked = data.unlocked ? 1 : 0

                    if (data.unlocked) {
                        success(
                            i18n.t('notification.unblockedSuccessfully', {
                                referenceDate,
                            })
                        )
                    } else {
                        success(
                            i18n.t('notification.blockedSuccessfully', {
                                referenceDate,
                            })
                        )
                    }
                })
                .catch((error) => {
                    console.log(error)
                    danger()
                })
        },
        retroactiveCheckIn(expectation) {
            let path = this.indicatorType == 'target' ? 'targets' : 'indicators'
            this.$router.push({
                path: `/v2/target/${path}/view/${expectation.indicator_id}/retroactive/${expectation.id}`,
            })
        },
        indicatorDetailsExpectancy(expectation) {
            return formatNumber(expectation) || '-'
        },
        indicatorExpectationResultLabel(expectation) {
            let label
            if (!expectation.checkins.length && !expectation.simulatedCheckins.length) {
                label = i18n.t('profile.table.result')
            } else if (expectation.checkins.length) {
                label = i18n.t('profile.table.result')
            } else {
                label = i18n.t('form.label.forecast')
            }

            return label + ' ' + this.indicatorExpectationResult(expectation)
        },
        indicatorExpectationResult(expectation) {
            return expectation.accomplished ? formatNumber(expectation.accomplished) : '-'
        },
        indicatorDetailsAchieved(expectation) {
            let accomplishedPercentage = expectation.headlight.accomplishedPercentage
            return accomplishedPercentage ? `${formatPercentage(accomplishedPercentage)}%` : '-'
        },
        expectationLockButtonLabel(expectation) {
            return expectation.unlocked ? i18n.t('block') : i18n.t('unblock')
        },
        disableCheckInButton(expectation) {
            return !(
                !!expectation.unlocked &&
                (this.isIndicatorOwnerOrManager ||
                    this.didCurrentUserContractThisIndicator ||
                    this.userStageCheckinPermission() ||
                    (this.isCurrentUserFocalPoint && this.userStageCheckinPermission())) &&
                !this.isTargetUnavailableToEdit()
            )
        },
        isTargetUnavailableToEdit() {
            return (this.targetIsCompleted() || this.isPeriodFinished()) && !this.userStageCheckinPermission()
        },
        userStageCheckinPermission() {
            const stagePermissions = this.currentUser.permissions.goals
            const checkinFocalPointPermission =
                this.isCurrentUserFocalPoint == true && !this.isIndicatorOwner && !this.hasRolesPermission
                    ? this.getCheckinFocalPointPermission()
                    : stagePermissions[this.indicator.level].checkin
            return this.hasRolesPermission || checkinFocalPointPermission
        },
        getCheckinFocalPointPermission() {
            if (this.indicator.focalPointsPermission.length == 0) return false
            return this.indicator.focalPointsPermission[0][`ID-${this.currentUser.id}`][this.indicator.level]
                .checkin_as_focal_point
        },
        targetIsCompleted() {
            return this.indicator.status === 'completed'
        },
        isPeriodFinished() {
            return this.indicator.period.status === 'finished'
        },
    },
    computed: {
        hasRolesPermission() {
            return this.userHasPermission(this.currentUser, this.availableRoles, [])
        },
        availableRoles() {
            return ['god', 'administrator', 'local_administrator', 'is_target_master']
        },
        isCurrentUserFocalPoint() {
            let isFocalPoint = false

            if (!this.indicator.departmentsFocalPointRelations) return

            const userSections = this.currentUser.sections.map((section) => section.id)

            const indicatorFocalPointSections = [
                ...this.indicator.departmentsFocalPointRelations,
                ...this.indicator.occupationGroupsFocalPointRelations,
            ].map((section) => section.id)

            userSections.forEach((id) => {
                if (indicatorFocalPointSections.includes(id) && !isFocalPoint) {
                    isFocalPoint = true
                }
            })

            const userRelations = this.indicator.userFocalPointRelations.map((user) => user.id)

            userRelations.forEach((id) => {
                if (userRelations.includes(id) && !isFocalPoint) {
                    isFocalPoint = true
                }
            })

            return isFocalPoint
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        kpiCheckins() {
            return this.indicator.kpiCheckins
        },
        didCurrentUserContractThisIndicator() {
            return this.indicator.indicatorUsersIds.includes(this.currentUser.id)
        },
        isAdmin() {
            return this.currentUser.role.name == 'administrator' || this.currentUser.role.name == 'local_administrator'
        },
        isTargetMaster() {
            return this.currentUser.is_target_master
        },
        isIndicatorOwner() {
            return this.currentUser.id == this.indicator.owner.id
        },
        isIndicatorOwnerOrManager() {
            return this.isIndicatorOwner || this.isManagerOfIndicatorOwner
        },
        isManagerOfIndicatorOwner() {
            return this.currentUser.id == this.indicator.owner.manager_id
        },
        showUnlockExpectationCheckIn() {
            return (
                (this.userStageCheckinPermission() && this.isCurrentUserFocalPoint && this.isIndicatorOwner) ||
                this.isIndicatorOwnerOrManager ||
                this.hasRolesPermission ||
                this.isManagerOfIndicatorOwner
            )
        },
        showCheckInDropdownItem() {
            return (
                this.userStageCheckinPermission() ||
                this.isManagerOfIndicatorOwner ||
                this.isIndicatorOwner ||
                this.didCurrentUserContractThisIndicator
            )
        },
        indicatorType() {
            return this.indicator.type == 'goal' ? 'target' : 'kpi'
        },
        indicatorTarget() {
            return this.indicator.target !== null ? formatNumber(this.indicator.target) : '-'
        },
        indicatorExpectancy() {
            return this.kpiCheckins.ytdCalculated !== null ? formatNumber(this.kpiCheckins.ytdCalculated) : '-'
        },
        indicatorCurrentResult() {
            return this.indicator.lastCheckInValue !== null ? formatNumber(this.indicator.lastCheckInValue) : '-'
        },
        indicatorExpectation() {
            return this.kpiCheckins.expectationByVisualizationType !== null
                ? formatNumber(this.kpiCheckins.expectationByVisualizationType)
                : '-'
        },
        indicatorForecast() {
            return this.kpiCheckins.simulated !== null ? formatNumber(this.kpiCheckins.simulated) : '-'
        },
        disableRetroactiveCheckInDropdown() {
            return !this.userStageCheckinPermission()
        },
    },
    filters: {
        capitalize: function (value) {
            if (!value) return ''
            value = value.toString()
            return value.charAt(0).toUpperCase() + value.slice(1)
        },
    },
}
</script>
<style scoped>
.division {
    width: 60px;
    margin: 0 auto;
    border: 0.5px solid grey;
}
.not-allowed {
    cursor: not-allowed;
}
</style>
